import { useState, useEffect } from 'react'
import { User, Loader2 } from 'lucide-react'
import { handleValidationErrors } from '@/shared/utils'
import PFASPersonalInfo from './PFASPersonalInfo'
import PFASContactInfo from './PFASContactInfo'
import PFASIdentityInfo from './PFASIdentityInfo'
import PFASLegalInfo from './PFASLegalInfo'
import type { Step3InjuredPartyProps, ValidationErrors } from '@/features/enrollment'

// PFAS-specific validation (no relationToVictim or isMinor required)
const validatePFASInjuredParty = (data: any) => {
  const errors: { [key: string]: string } = {}
  
  if (!data.injuredPartyFirstName) errors.injuredPartyFirstName = 'First Name is required'
  if (!data.injuredPartyLastName) errors.injuredPartyLastName = 'Last Name is required'
  if (!data.injuredPartyEmail) errors.injuredPartyEmail = 'Email is required'
  if (!data.gender) errors.gender = 'Gender is required'
  if (!data.injuredPartyAddressLine1) errors.injuredPartyAddressLine1 = 'Address Line 1 is required'
  if (!data.injuredPartyCity) errors.injuredPartyCity = 'City is required'
  if (!data.injuredPartyState) errors.injuredPartyState = 'State is required'
  if (!data.injuredPartyZipCode) errors.injuredPartyZipCode = 'ZIP Code is required'
  if (!data.phoneNumber) errors.phoneNumber = 'Phone Number is required'
  if (!data.injuredPartyDOB) errors.injuredPartyDOB = 'Date of Birth is required'
  
  return errors
}

export default function PFASInjuredParty({
  enrollmentData,
  updateEnrollmentData,
  handleNavigate,
  handleSimpleNavigate,
  currentStepIndex,
  totalSteps = 8,
  isNavigating,
}: Step3InjuredPartyProps) {
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false)

  const computedCurrentStepIndex = currentStepIndex ?? 3
  const computedNextStepIndex = Math.min(totalSteps - 1, computedCurrentStepIndex + 1)
  const computedPreviousStepIndex = Math.max(0, computedCurrentStepIndex - 1)

  // Auto-populate fields from PFAS Step 1 (OptInForm)
  useEffect(() => {
    if (!hasAutoPopulated) {
      if (!enrollmentData.injuredPartyFirstName && enrollmentData.firstName) {
        updateEnrollmentData('injuredPartyFirstName', enrollmentData.firstName)
      }
      if (!enrollmentData.injuredPartyLastName && enrollmentData.lastName) {
        updateEnrollmentData('injuredPartyLastName', enrollmentData.lastName)
      }
      if (!enrollmentData.injuredPartyEmail && enrollmentData.email) {
        updateEnrollmentData('injuredPartyEmail', enrollmentData.email)
      }
      if (!enrollmentData.phoneNumber && enrollmentData.phone) {
        updateEnrollmentData('phoneNumber', enrollmentData.phone)
      }

      setHasAutoPopulated(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrollmentData.firstName,
    enrollmentData.lastName, 
    enrollmentData.email,
    enrollmentData.phone,
    enrollmentData.injuredPartyFirstName,
    enrollmentData.injuredPartyLastName,
    enrollmentData.injuredPartyEmail,
    enrollmentData.phoneNumber,
    hasAutoPopulated
  ])

  // Auto-populate retainer agreement full name
  useEffect(() => {
    const firstName = enrollmentData.firstName || enrollmentData.injuredPartyFirstName
    const lastName = enrollmentData.lastName || enrollmentData.injuredPartyLastName
    
    if (!enrollmentData.retainerAgreementFullName && firstName && lastName) {
      updateEnrollmentData('retainerAgreementFullName', `${firstName} ${lastName}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrollmentData.firstName,
    enrollmentData.lastName,
    enrollmentData.injuredPartyFirstName,
    enrollmentData.injuredPartyLastName,
    enrollmentData.retainerAgreementFullName
  ])

  const runValidation = () => {
    const validationErrors = validatePFASInjuredParty(enrollmentData)
    console.log('PFAS Validation errors:', validationErrors)
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length === 0) {
      return true
    }

    handleValidationErrors(validationErrors)
    return false
  }

  const handleContinue = async () => {
    const isValid = runValidation()
    if (!isValid) {
      return
    }

    try {
      await handleNavigate(computedNextStepIndex)
    } catch (error) {
      console.error('❌ PFASInjuredParty - Navigation failed:', error)
      alert('Failed to save your progress. Please try again.')
    }
  }

  const handleBack = () => {
    setErrors({})
    const targetIndex = computedPreviousStepIndex
    if (handleSimpleNavigate) {
      handleSimpleNavigate(targetIndex)
    } else {
      handleNavigate(targetIndex)
    }
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
          <User className="w-6 h-6 text-primary-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-accent-900">Affected Person Details</h3>
          <p className="text-sm text-accent-600">Collect essential information about the person affected by PFAS contamination.</p>
        </div>
      </div>

      <div className="space-y-6">
        <PFASPersonalInfo
          enrollmentData={enrollmentData}
          updateEnrollmentData={updateEnrollmentData}
          errors={errors}
        />

        <PFASContactInfo
          enrollmentData={enrollmentData}
          updateEnrollmentData={updateEnrollmentData}
          errors={errors}
        />

        <PFASIdentityInfo
          enrollmentData={enrollmentData}
          updateEnrollmentData={updateEnrollmentData}
          errors={errors}
        />

        <PFASLegalInfo
          enrollmentData={enrollmentData}
          updateEnrollmentData={updateEnrollmentData}
          errors={errors}
        />
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={handleBack}
          disabled={isNavigating}
          className="btn-outline flex items-center gap-2"
        >
          <span>←</span>
          <span>Back</span>
        </button>

        <button
          onClick={handleContinue}
          disabled={isNavigating}
          className="btn-primary flex items-center gap-2"
        >
          {isNavigating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <span>→</span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}
